import { Box } from '@mui/material';
import CustomDatePicker from 'components/DatePIcker/CustomDatePicker';
import dayjs, { type Dayjs } from 'dayjs';
import { useMemo, useState } from 'react';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import TireProfileHandler from 'handlers/tire-pressure';
import { useAppSelector } from 'store/hook';
import { CustomLoadingButton } from 'components/Buttons/CustomLoadingButton/CustomLoadingButton';
import { saveAs } from 'file-saver';
import useCustomerInfo from 'hooks/useCustomerInfo';
import { useTranslation } from 'react-i18next';

dayjs.extend(utc);
dayjs.extend(timezone);

export const ReportDownload = () => {
  const { t } = useTranslation();
  const tireProfileHandler = new TireProfileHandler();
  const customerCode = useAppSelector(
    (state) => state.authReducer.customer.code
  );

  const { customerTz } = useCustomerInfo();

  const [selectedMonth, setSelectedMonth] = useState<Dayjs | null>(dayjs());
  const [isLoading, setIsLoading] = useState(false);

  const { minMonth, maxMonth } = useMemo(() => {
    const userNow = dayjs().tz(customerTz);
    return {
      minMonth: dayjs.tz('2023-01-01T00:00:00', customerTz).startOf('month'),
      maxMonth: userNow.endOf('month'),
    };
  }, [customerTz]);

  const getUtcRangeForMonth = (month: Dayjs | null) => {
    if (!month) return null;

    const localStart = month.startOf('month');
    const localEnd = month.endOf('month');

    const utcStart = localStart.tz(customerTz, true).utc().format();
    const utcEnd = localEnd.tz(customerTz, true).utc().format();

    return { utcStart, utcEnd };
  };

  const handleDownload = async () => {
    try {
      setIsLoading(true);
      const utcRange = getUtcRangeForMonth(selectedMonth);
      if (!utcRange) return;

      const response = await tireProfileHandler.downloadReport(
        utcRange.utcStart,
        utcRange.utcEnd,
        customerCode
      );

      const monthName = dayjs(selectedMonth).tz(customerTz).format('MMMM_YYYY'); // e.g., "October_2025"
      const fileName = `Tire_Performance_Report_${monthName}.pdf`;
      const blob = new Blob([response.data], { type: 'application/pdf' });
      saveAs(blob, fileName);
    } catch (error) {
      console.error('Error while downloading report');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Box display={'flex'} flexDirection={'row'} gap={1} alignItems={'center'}>
      {customerTz ? (
        <CustomDatePicker
          views={['month', 'year']}
          value={selectedMonth}
          timezone={customerTz}
          onChange={(newValue) => {
            setSelectedMonth(newValue);
          }}
          format="MMMM YYYY"
          maxDate={maxMonth}
          minDate={minMonth}
          sx={{
            width: '200px',
          }}
        />
      ) : null}

      <CustomLoadingButton
        variant="contained"
        color="primary"
        onClick={handleDownload}
        disabled={!selectedMonth || isLoading}
        loading={isLoading}
        sx={{
          height: '36px',
          width: '160px',
        }}
      >
        {t('download_report')}
      </CustomLoadingButton>
    </Box>
  );
};
